import axios from 'axios'
import { API_BASE_URL } from '@/config/setting'
const api = axios.create({ baseURL: API_BASE_URL, withCredentials: false })
const sessionKey = `portal-session:${API_BASE_URL}`
let memoryToken = ''
function saveSessionToken(token) {
  memoryToken = token
  try {
    localStorage.setItem(sessionKey, token)
    try { sessionStorage.removeItem(sessionKey) } catch (_) { /* Storage may be unavailable. */ }
    return
  } catch (_) { /* Fall back to tab storage when persistent storage is unavailable. */ }
  try { sessionStorage.setItem(sessionKey, token) } catch (_) { /* Keep the session in memory. */ }
}
function readSessionToken() {
  try {
    const token = localStorage.getItem(sessionKey)
    if (token !== null) return token
  } catch (_) { /* Try the existing tab session. */ }
  try {
    const token = sessionStorage.getItem(sessionKey)
    if (token) { saveSessionToken(token); return token }
  } catch (_) { /* Use the in-memory fallback. */ }
  return memoryToken
}
function clearSessionToken() {
  memoryToken = ''
  try { localStorage.removeItem(sessionKey) } catch (_) { /* Storage may be unavailable. */ }
  try { sessionStorage.removeItem(sessionKey) } catch (_) { /* Storage may be unavailable. */ }
}
window.addEventListener('storage', event => {
  if (event.key !== sessionKey || event.newValue !== null || event.storageArea !== localStorage) return
  memoryToken = ''
  try { sessionStorage.removeItem(sessionKey) } catch (_) { /* Remove legacy tab sessions too. */ }
  window.dispatchEvent(new Event('portal-session-expired'))
})
api.interceptors.request.use(config => {
  const token = readSessionToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})
api.interceptors.response.use(response => {
  if (response.data.sessionToken) saveSessionToken(response.data.sessionToken)
  if (response.config.params?.action === 'logout') clearSessionToken()
  return response
}, error => {
  const serverError = error.response?.data?.error
  error.message = typeof serverError === 'string' && serverError ? serverError : 'Не удалось связаться с сервером. Повторите позже.'
  if (error.response?.status === 404) {
    const action = error.config?.params?.action
    const request = `${API_BASE_URL}${action ? `?action=${encodeURIComponent(action)}` : ''}`
    error.message = typeof serverError === 'string' && serverError
      ? `${serverError} (HTTP 404, запрос: ${request})`
      : `Сервер вернул HTTP 404: ${request}. Проверьте путь к прокси и адрес API на сервере прокси (PORTAL_UPSTREAM_URL).`
  }
  if (error.response?.status === 401) {
    const currentToken = readSessionToken()
    // A delayed response from an older session must not clear a newer login.
    if (!currentToken || error.config?.headers?.Authorization === `Bearer ${currentToken}`) {
      clearSessionToken()
      window.dispatchEvent(new Event('portal-session-expired'))
    }
  }
  return Promise.reject(error)
})
export const get = (action, params = {}) => api.get('', { params: { action, ...params } }).then(r => r.data)
export const post = (action, data, csrf) => api.post('', data, { params: { action }, headers: { 'X-CSRF-Token': csrf } }).then(r => r.data)
export async function download(kind, id, index) {
  try {
    const response = await api.get('', { params: { action: 'download', kind, id, index }, responseType: 'blob' })
    const encoded = (response.headers['content-disposition'] || '').match(/filename\*=UTF-8''(.+)/)?.[1]
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = encoded ? decodeURIComponent(encoded) : 'document'
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try { error.message = JSON.parse(await error.response.data.text()).error } catch { /* Use default message. */ }
    }
    throw error
  }
}
export default api
